import React, { useEffect } from "react";
import {
  CCard,
  CCardBody,
  CCardTitle,
  CCol,
  CRow,
  CTable,
  CTableBody,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableDataCell,
  CButton,
} from "@coreui/react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import StatCardWithChart from "../../components/seller/StatCardWithChart";
import { useDispatch, useSelector } from "react-redux";
import { fetchDashboardData } from "../../features/seller/sellerSlice";
import {
  cilBan,
  cilCheckCircle,
  cilClock,
  cilDollar,
  cilList,
  cilPlus,
  cilShareBoxed,
  cilThumbUp,
  cilUser,
} from "@coreui/icons";
import { capitalizeFirst } from "../../utils/stringUtils";
import AppLoader from "../../components/seller/AppLoader";
import MetalPriceChart from "../../components/seller/MetalPriceChart";
import { formatCurrency, formatPrice } from "../../utils/price";
import { useNavigate } from "react-router-dom";
import CIcon from "@coreui/icons-react";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const Dashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: dashboardData, loading } = useSelector(
    (state) => state.seller.dashboard
  );
  const recentOrders = dashboardData?.recent_orders ?? [];
  const lowStockProducts = dashboardData?.low_stock_products ?? [];

  useEffect(() => {
    dispatch(fetchDashboardData())
      .unwrap()
      .then((data) => {
        // Optional: You can perform side-effects here like toast
        // console.log("Dashboard fetched:", data);
      })
      .catch((err) => {
        console.error("Failed to fetch dashboard:", err);
      });
  }, [dispatch]);

  // Navigation hook for card clicks
  const dashboardRoutes = {
    totalOrders: "/seller/view-orders",
    rejectedProducts: "/seller/products?status=rejected",
    completedOrders: "/seller/view-orders?status=completed",
    pendingApproval: "/seller/products?status=pending",
    totalProducts: "/seller/products",
    approvedProducts: "/seller/products?status=approved",
    pendingOrders: "/seller/view-orders?status=pending",
    totalSales: "/seller/view-orders?status=completed",
  };

  const handleCardClick = (key) => {
    if (dashboardRoutes[key]) {
      navigate(dashboardRoutes[key]);
    }
  };

  const statCards = [
    {
      key: "totalProducts",
      title: "Total Products",
      icon: cilShareBoxed,
      color: "primary",
      value: dashboardData?.total_products || 0,
    },
    {
      key: "pendingApproval",
      title: "Pending Approval",
      icon: cilClock,
      color: "warning",
      value: dashboardData?.pending_products || 0,
    },
    {
      key: "approvedProducts",
      title: "Approved Products",
      icon: cilThumbUp,
      color: "success",
      value: dashboardData?.approved_products || 0,
    },
    {
      key: "rejectedProducts",
      title: "Rejected Products",
      icon: cilBan,
      color: "danger",
      value: dashboardData?.rejected_products || 0,
    },

    {
      key: "totalOrders",
      title: "Total Orders",
      icon: cilList,
      color: "info",
      value: dashboardData?.total_orders || 0,
    },

    {
      key: "pendingOrders",
      title: "Pending Orders",
      icon: cilClock,
      color: "warning",
      value: dashboardData?.pending_orders || 0,
    },
    {
      key: "completedOrders",
      title: "Completed Orders",
      icon: cilCheckCircle,
      color: "success",
      value: dashboardData?.completed_orders || 0,
    },
    {
      key: "totalSales",
      title: "Total Sales",
      icon: cilDollar,
      color: "secondary",
      value: formatCurrency(dashboardData?.total_sales),
    },
  ];

  const getStatusBadgeClass = (status) => {
    switch (status.toLowerCase()) {
      case "pending":
        return "badge bg-warning text-dark";
      case "accepted":
        return "badge bg-info text-dark";
      case "matched":
        return "badge bg-primary";
      case "completed":
        return "badge bg-success";
      case "rejected":
        return "badge bg-danger";
      default:
        return "badge bg-secondary";
    }
  };

  const ORDER_STATUS_LABELS = {
    pending: "Pending",
    accepted: "Accepted",
    matched: "Matched",
    completed: "Completed",
    rejected: "Rejected",

    admin_review: "Admin Review",
    awaiting_extra_payment: "Awaiting Extra Payment",
  };

  const getOrderStatusLabel = (status) => {
    if (!status) return "-";

    return (
      ORDER_STATUS_LABELS[status] ||
      status
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase())
    );
  };

  if (loading) return <AppLoader />;

  return (
    <div>
      <CRow className="mb-3">
        <CCol xs="12" className="d-flex justify-content-end gap-2">
          <CButton
            color="primary"
            variant="outline"
            onClick={() => navigate("/seller/products")}
          >
            <CIcon icon={cilPlus} className="me-2" />
            Create Bid
          </CButton>

          <CButton
            color="secondary"
            variant="outline"
            onClick={() => navigate("/seller/buyer-offers")}
          >
            <CIcon icon={cilUser} className="me-2" />
            Buyer Offers
          </CButton>
        </CCol>
      </CRow>
      <CRow className="mb-4">
        <StatCardWithChart data={statCards} onCardClick={handleCardClick} />
      </CRow>

      {/* <MetalPriceChart /> */}

      <CRow>
        <CCol>
          <CCard>
            <CCardBody>
              <CCardTitle className="mb-3">Recent Orders</CCardTitle>
              <CTable responsive bordered hover align="middle">
                <CTableHead color="light">
                  <CTableRow>
                    <CTableHeaderCell>Order ID</CTableHeaderCell>
                    <CTableHeaderCell>Product</CTableHeaderCell>
                    <CTableHeaderCell>Amount (AUD)</CTableHeaderCell>
                    <CTableHeaderCell>Status</CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {recentOrders.length > 0 ? (
                    recentOrders.map((order, index) => (
                      <CTableRow
                        key={index}
                        style={{ cursor: "pointer" }}
                        onClick={() =>
                          navigate(`/seller/order-detail/${order.id}`)
                        }
                      >
                        <CTableDataCell>{order.id ?? "-"}</CTableDataCell>
                        <CTableDataCell>
                          {order.product_name || "-"}
                        </CTableDataCell>
                        <CTableDataCell>
                          {formatCurrency(order.total_price)}
                          {/* {order.total_price != null
                            ? `A$ ${formatPrice(order.total_price)}`
                            : "-"} */}
                        </CTableDataCell>
                        <CTableDataCell>
                          {order.status ? (
                            <span className={getStatusBadgeClass(order.status)}>
                              {getOrderStatusLabel(order.status)}
                            </span>
                          ) : (
                            "-"
                          )}
                        </CTableDataCell>
                      </CTableRow>
                    ))
                  ) : (
                    <CTableRow>
                      <CTableDataCell
                        colSpan="4"
                        className="text-center text-muted"
                      >
                        No recent order available.
                      </CTableDataCell>
                    </CTableRow>
                  )}
                </CTableBody>
              </CTable>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <CRow className="mt-4 mb-4">
        <CCol>
          <CCard>
            <CCardBody>
              <CCardTitle className="mb-3">Low Stock Alerts</CCardTitle>
              <CTable responsive bordered hover align="middle">
                <CTableHead color="light">
                  <CTableRow>
                    <CTableHeaderCell>#</CTableHeaderCell>
                    <CTableHeaderCell>Product</CTableHeaderCell>
                    <CTableHeaderCell>Stock</CTableHeaderCell>
                    <CTableHeaderCell>Status</CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {lowStockProducts.length > 0 ? (
                    lowStockProducts.map((item, index) => (
                      <CTableRow
                        key={item.id ?? index}
                        style={{ cursor: "pointer" }}
                        onClick={() => navigate(`/seller/products/${item.id}`)}
                      >
                        <CTableDataCell>{index + 1}</CTableDataCell>
                        <CTableDataCell>{item.name || "-"}</CTableDataCell>
                        <CTableDataCell>
                          {item.quantity != null ? item.quantity : "-"}
                        </CTableDataCell>
                        <CTableDataCell>
                          <span className="badge bg-danger">Low</span>
                        </CTableDataCell>
                      </CTableRow>
                    ))
                  ) : (
                    <CTableRow>
                      <CTableDataCell
                        colSpan="4"
                        className="text-center text-muted"
                      >
                        No stock data available.
                      </CTableDataCell>
                    </CTableRow>
                  )}
                </CTableBody>
              </CTable>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
    </div>
  );
};

export default Dashboard;
